/**
 * Shared schema toolkit for contract interfaces.
 *
 * TypeBox `Type.Object(...)` definitions ARE JSON Schema — the OpenAPI generator serialises
 * them directly. `createSchema` derives the runtime validator (`safeParse`/`parse`, Zod-shaped
 * result) from that same definition, so field lists are never written twice.
 *
 * Stable util — do not add domain-specific contracts here.
 */
import { FormatRegistry, Kind, Type, TypeRegistry, type SchemaOptions, type Static, type TSchema } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

// HTML5/WHATWG email pattern (same family AJV uses for format: email).
FormatRegistry.Set('email', (value) =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(value),
);

FormatRegistry.Set('date', (value) => /^\d{4}-\d{2}-\d{2}$/.test(value));
FormatRegistry.Set('date-time', (value) => Number.isFinite(Date.parse(value)));

// Emits `{ type: 'string', enum: [...] }` (cleaner OpenAPI than TypeBox's anyOf-of-literals).
TypeRegistry.Set('StringEnum', (schema: { enum: string[] }, value) => typeof value === 'string' && schema.enum.includes(value));

export const StringEnum = <T extends string[]>(values: [...T], options: SchemaOptions = {}) =>
  Type.Unsafe<T[number]>({ ...options, [Kind]: 'StringEnum', type: 'string', enum: values });

export type ValidationIssue = { path: string; message: string };

export class ContractValidationError extends Error {
  readonly issues: ValidationIssue[];
  constructor(issues: ValidationIssue[]) {
    super(issues[0]?.message ?? 'Validation failed');
    this.name = 'ContractValidationError';
    this.issues = issues;
  }
}

export type SafeParseResult<T> = { success: true; data: T } | { success: false; error: ContractValidationError };

export type ContractSchema<S extends TSchema> = {
  jsonSchema: S;
  safeParse(input: unknown): SafeParseResult<Static<S>>;
  parse(input: unknown): Static<S>;
};

export function createSchema<S extends TSchema>(schema: S): ContractSchema<S> {
  return {
    jsonSchema: schema,
    safeParse(input: unknown): SafeParseResult<Static<S>> {
      const value = Value.Clean(schema, Value.Default(schema, Value.Clone(input)));
      if (Value.Check(schema, value)) {
        return { success: true, data: value };
      }
      const issues = [...Value.Errors(schema, value)].map(({ path, message }) => ({
        path,
        message: path ? `${path}: ${message}` : message,
      }));
      return { success: false, error: new ContractValidationError(issues) };
    },
    parse(input: unknown): Static<S> {
      const result = this.safeParse(input);
      if (!result.success) {
        throw result.error;
      }
      return result.data;
    },
  };
}
